import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/apiService';
import Banner from '../Banner/Banner';
import SectionPliable from '../SectionPliable/SectionPliable';
import Button from '../Button/Button';
import type { DerniereMaj } from '../EstimationBesoins/EstimationBesoins';
import type { PerimetreValue } from '../PerimetreSelector/PerimetreSelector';
import PerimetreCarteModal from './PerimetreCarteModal';
import { repartir, tampon, round2 } from './localisationRepartition';
import '../EstimationBesoins/EstimationBesoins.css';
import './LocalisationBesoins.css';

interface DepZone {
  id: number;
  nom: string;
}
interface RegionZone {
  id_region: number;
  region_nom: string;
  region_code: string | null;
  departements: DepZone[];
}

// Reponse de GET /localisation-besoins.
interface LocalisationData {
  annees: number[];
  zones: RegionZone[];
  passe: Record<string, Record<string, number>>;       // % passe calcule (bdc_archive)
  previsions: Record<string, Record<string, number>>;  // % previsions saisies (annee en cours + futures)
  derniere_maj: DerniereMaj | null;
  organisme_id?: number;
  perimetre_detail?: PerimetreValue | null; // perimetre geographique d'achat (source des zones)
  perimetre_dep_ids?: number[];             // departements EDITABLES (perimetre) ; les autres = historique seul
}

interface LocalisationBesoinsProps {
  onMajChange?: (maj: DerniereMaj | null) => void;
  // Notifie le parent (Profil) qu'on vient de modifier le perimetre depuis cette section,
  // pour que l'onglet Presentation (selecteur de perimetre) reflete la modif immediatement.
  onPerimetreChange?: (value: PerimetreValue) => void;
}

const FUTURE_HORIZON = 3;

/**
 * Section "Localisation en % du volume de commande" (onglet acheteur), table soeur de
 * l'estimation financiere : repartition en % par departement (lot geographique), groupes
 * par region.
 * - annees passees : repartition reelle (bdc_archive), lecture seule.
 * - annee en cours + suivantes : previsions en % saisies. La DERNIERE ligne (dernier
 *   departement de l'ordre d'affichage) est une ligne TAMPON, en lecture seule : elle vaut
 *   100 - somme des autres, de sorte que chaque annee totalise toujours 100 %.
 *
 * Valeurs par defaut (deterministes, recalculees a chaque chargement, NON ecrites tant que
 * l'acheteur n'a rien saisi) : un organisme avec historique part de la repartition de sa
 * derniere annee connue ; un organisme sans historique part d'une repartition equitable.
 * Une annee n'est persistee qu'au premier edit, en une seule sauvegarde atomique de la
 * colonne complete (cf. apiService.saveLocalisationAnnee). Les decimales sont autorisees.
 */
const LocalisationBesoins: React.FC<LocalisationBesoinsProps> = ({ onMajChange, onPerimetreChange }) => {
  const [data, setData] = useState<LocalisationData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  // Saisies de l'acheteur, en surcouche des valeurs persistees / par defaut. Cle = id dep -> annee -> string.
  const [edits, setEdits] = useState<Record<string, Record<number, string>>>({});
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved'>('idle');
  const [tip, setTip] = useState<{ x: number; y: number; text: string } | null>(null);
  // Modale carte du perimetre (edition des zones = edition du perimetre d'achat).
  const [perimetreModalOpen, setPerimetreModalOpen] = useState<boolean>(false);
  const [perimetreSaving, setPerimetreSaving] = useState<boolean>(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const yearColRef = useRef<HTMLTableCellElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const lotThRef = useRef<HTMLTableCellElement | null>(null);
  const libelleThRef = useRef<HTMLTableCellElement | null>(null);
  const ywRef = useRef(0);
  // Annees modifiees en attente de flush (sauvegarde par colonne complete).
  const dirtyYears = useRef<Set<number>>(new Set());
  const saveTimer = useRef<number | null>(null);

  const currentYear = new Date().getFullYear();

  // Annees affichees : plus ancienne annee passee avec donnees -> annee en cours + 3 futures.
  const years = useMemo(() => {
    const pasts = (data?.annees ?? []).filter((y) => y < currentYear);
    let earliest: number;
    if (pasts.length) earliest = Math.min(...pasts);
    else if (loading) earliest = currentYear - 3;
    else earliest = currentYear;
    const arr: number[] = [];
    for (let y = earliest; y <= currentYear + FUTURE_HORIZON; y++) arr.push(y);
    return arr;
  }, [data, loading, currentYear]);

  // Zones affichees = zones du backend = departements du PERIMETRE geographique d'achat.
  const zones = useMemo<RegionZone[]>(() => {
    const list = (data?.zones ?? []).map((z) => ({ ...z, departements: [...z.departements] }));
    list.forEach((z) => z.departements.sort((a, b) => a.id - b.id));
    list.sort((a, b) => a.region_nom.localeCompare(b.region_nom));
    return list;
  }, [data]);

  // Departements dans l'ordre d'affichage (region puis id). Le dernier = ligne tampon.
  const orderedDeps = useMemo<number[]>(() => zones.flatMap((z) => z.departements.map((d) => d.id)), [zones]);
  // Departements editables = perimetre d'achat. Les autres lignes affichees (historique d'un
  // departement retire du perimetre) restent visibles mais en lecture seule, hors repartition.
  const editableDepIds = useMemo<Set<number>>(() => new Set((data?.perimetre_dep_ids ?? []).map(Number)), [data]);
  const isEditableDep = (depId: number) => (editableDepIds.size === 0 ? true : editableDepIds.has(depId));
  const editableDeps = useMemo<number[]>(
    () => orderedDeps.filter((d) => (editableDepIds.size === 0 ? true : editableDepIds.has(d))),
    [orderedDeps, editableDepIds],
  );
  const balancerId = useMemo<number | null>(() => (editableDeps.length ? editableDeps[editableDeps.length - 1] : null), [editableDeps]);

  // Derniere annee d'historique disponible (sert de base aux valeurs par defaut du futur).
  const lastHistYear = useMemo<number | null>(() => {
    const past = (data?.annees ?? []).filter((y) => y < currentYear);
    return past.length ? Math.max(...past) : null;
  }, [data, currentYear]);

  // Repartition par defaut (deterministe) : historique re-normalise sur le perimetre, sinon equitable.
  const defaultDist = useMemo<Record<number, number>>(() => {
    if (!editableDeps.length) return {};
    let poids: Record<number, number> | undefined;
    if (lastHistYear !== null) {
      const p: Record<number, number> = {};
      let sum = 0;
      editableDeps.forEach((d) => {
        const v = data?.passe?.[String(d)]?.[String(lastHistYear)] ?? 0;
        p[d] = v;
        sum += v;
      });
      if (sum > 0) poids = p; // historique entierement hors perimetre -> on retombe sur l'equitable
    }
    return repartir(editableDeps, poids);
  }, [editableDeps, lastHistYear, data]);

  const yearsRef = useRef(years);
  yearsRef.current = years;
  const dataSettled = !loading;
  const dataSettledRef = useRef(dataSettled);
  dataSettledRef.current = dataSettled;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiService.getLocalisationBesoins()
      .then((d: LocalisationData | null) => {
        if (cancelled || !d) return;
        setData(d);
        onMajChange?.(d.derniere_maj ?? null);
        setEdits({});
      })
      .catch(() => undefined)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Re-derive les zones apres une edition du perimetre (sans toucher aux saisies en cours).
  const refetchZones = useCallback(() => {
    apiService.getLocalisationBesoins()
      .then((d: LocalisationData | null) => { if (d) setData(d); })
      .catch(() => undefined);
  }, []);

  // Sauvegarde du perimetre depuis la carte de la modale -> etend/modifie le perimetre
  // d'achat, puis re-derive les zones du tableau.
  const handleSavePerimetre = useCallback((value: PerimetreValue) => {
    const id = data?.organisme_id;
    if (!id) { setPerimetreModalOpen(false); return; }
    setPerimetreSaving(true);
    apiService.saveOrganismePerimetre(id, {
      perimetre_type: value.type === 'national' ? 'national' : 'zones',
      region_ids: value.regions.map((r) => r.id),
      departement_ids: value.departements.map((d) => d.id),
    })
      .then(() => {
        refetchZones();
        setPerimetreModalOpen(false);
        // Propage au parent : l'onglet Presentation doit refleter ce nouveau perimetre tout de suite.
        onPerimetreChange?.(value);
      })
      .catch(() => undefined)
      .finally(() => setPerimetreSaving(false));
  }, [data, refetchZones, onPerimetreChange]);

  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false);
  // Fleches flottantes : zone des annees (apres colonnes figees), centrees sur 1ere/derniere
  // colonne visible, a hauteur de l'entete.
  const [arrowBox, setArrowBox] = useState<{ left: number; right: number; height: number; pad: number }>({ left: 0, right: 0, height: 0, pad: 0 });
  const [ready, setReady] = useState<boolean>(false);

  // Mise en page identique a l'estimation (cf. EstimationBesoins) : mesure auto puis fixe,
  // toutes les annees tiennent dans la largeur disponible (pas de scroll horizontal).
  const recompute = useCallback((): number => {
    const wrap = scrollRef.current;
    const table = tableRef.current;
    if (!wrap || !table) return 0;
    const savedScroll = wrap.scrollLeft;
    const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
    const nbYears = yearsRef.current.length || 1;

    table.style.tableLayout = 'auto';
    table.style.width = 'auto';
    table.style.removeProperty('--lot-w');
    table.style.removeProperty('--libelle-w');
    table.style.removeProperty('--year-col-w');
    const lotW = lotThRef.current?.offsetWidth ?? 0;
    const libW = Math.min(libelleThRef.current?.offsetWidth ?? 0, Math.round(24 * remPx));

    const visibleCols = nbYears;
    const avail = wrap.clientWidth - lotW - libW;
    const yw = Math.max(Math.round(8 * remPx), Math.floor(avail / visibleCols));
    const reste = avail - visibleCols * yw;
    const libAdj = reste > 0 ? libW + reste : libW;

    table.style.setProperty('--lot-w', `${lotW}px`);
    table.style.setProperty('--libelle-w', `${libAdj}px`);
    table.style.setProperty('--year-col-w', `${yw}px`);
    table.style.tableLayout = 'fixed';
    table.style.width = `${lotW + libAdj + nbYears * yw}px`;

    ywRef.current = yw;
    // Fleches vers les EXTREMITES de la zone d'annees, avec un petit retrait du bord.
    setArrowBox({
      left: lotW + libAdj,
      right: Math.max(0, wrap.offsetWidth - wrap.clientWidth),
      height: yearColRef.current?.offsetHeight ?? 0,
      pad: Math.round(0.6 * remPx),
    });
    wrap.scrollLeft = savedScroll;
    setCanScrollLeft(wrap.scrollLeft > 1);
    setCanScrollRight(wrap.scrollLeft < wrap.scrollWidth - wrap.clientWidth - 1);
    return yw;
  }, []);

  const centerScroll = useCallback((node: HTMLDivElement, smooth: boolean) => {
    const yw = ywRef.current;
    const maxScroll = Math.max(0, node.scrollWidth - node.clientWidth);
    const idx = yearsRef.current.indexOf(currentYear);
    const dest = Math.min(Math.max(0, idx - 2) * yw, maxScroll);
    node.scrollTo({ left: dest, behavior: smooth ? 'smooth' : 'auto' });
    setCanScrollLeft(dest > 1);
    setCanScrollRight(dest < maxScroll - 1);
  }, [currentYear]);

  const wrapCleanup = useRef<(() => void) | null>(null);
  const setWrap = useCallback((node: HTMLDivElement | null) => {
    wrapCleanup.current?.();
    wrapCleanup.current = null;
    scrollRef.current = node;
    if (!node) { setReady(false); return; }
    setReady(false);
    const onScroll = () => {
      setCanScrollLeft(node.scrollLeft > 1);
      setCanScrollRight(node.scrollLeft < node.scrollWidth - node.clientWidth - 1);
    };
    node.addEventListener('scroll', onScroll, { passive: true });
    let lastW = node.clientWidth;
    const ro = new ResizeObserver(() => {
      const w = node.clientWidth;
      if (w === lastW) return;
      lastW = w;
      recompute();
      centerScroll(node, false);
    });
    ro.observe(node);
    recompute();
    let raf = 0;
    if (dataSettledRef.current) {
      raf = requestAnimationFrame(() => { centerScroll(node, true); setReady(true); });
    }
    wrapCleanup.current = () => {
      node.removeEventListener('scroll', onScroll);
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [recompute, centerScroll]);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    recompute();
    if (dataSettled) {
      const raf = requestAnimationFrame(() => { centerScroll(node, true); setReady(true); });
      return () => cancelAnimationFrame(raf);
    }
  }, [data, loading, dataSettled, zones, recompute, centerScroll]);

  const scrollYears = (dir: 1 | -1) => {
    const wrap = scrollRef.current;
    if (!wrap) return;
    const col = ywRef.current || yearColRef.current?.offsetWidth || 100;
    wrap.scrollBy({ left: dir * col, behavior: 'smooth' });
  };

  const isEditable = (year: number) => year >= currentYear;
  const isBalancer = (depId: number) => depId === balancerId;
  const keyOf = (depId: number, year: number) => `${depId}:${year}`;
  const fmtPct = (n: number) => `${round2(n)} %`;

  // Valeur persistee d'une cellule de prevision (table localisation_besoins), ou undefined.
  const savedVal = (depId: number, year: number): number | undefined => {
    const v = data?.previsions?.[String(depId)]?.[String(year)];
    return typeof v === 'number' ? v : undefined;
  };
  // Une annee est "initialisee" des qu'une cellule a ete saisie/persistee ; sinon on affiche les defauts.
  const yearInitialized = (year: number): boolean => editableDeps.some((d) => savedVal(d, year) !== undefined);

  // Valeur de base (hors saisie en cours) d'une cellule editable : persistee si l'annee est
  // initialisee, sinon la valeur par defaut deterministe.
  const baseValue = (depId: number, year: number): number => {
    if (yearInitialized(year)) return savedVal(depId, year) ?? 0;
    return defaultDist[depId] ?? 0;
  };
  // Valeur courante d'une cellule editable NON tampon (saisie en cours prioritaire).
  const editableNumber = (depId: number, year: number): number => {
    const e = edits[String(depId)]?.[year];
    if (e !== undefined) {
      const n = parseFloat(e);
      return Number.isFinite(n) ? n : 0;
    }
    return baseValue(depId, year);
  };
  const otherValues = (year: number): number[] => editableDeps.filter((d) => d !== balancerId).map((d) => editableNumber(d, year));

  // Valeur affichee d'un dep/annee : passe (lecture seule), tampon (calcule), ou prevision.
  const displayValue = (depId: number, year: number): number => {
    if (!isEditable(year)) return data?.passe?.[String(depId)]?.[String(year)] ?? 0;
    if (!isEditableDep(depId)) return 0; // departement hors perimetre : pas de prevision future
    if (depId === balancerId) return tampon(otherValues(year));
    return editableNumber(depId, year);
  };

  // Valeur brute (string) montree pendant la saisie d'une cellule editable.
  const rawValue = (depId: number, year: number): string => {
    const e = edits[String(depId)]?.[year];
    if (e !== undefined) return e;
    const base = baseValue(depId, year);
    return String(round2(base));
  };

  const regionTotal = (zone: RegionZone, year: number): number =>
    zone.departements.reduce((s, d) => s + displayValue(d.id, year), 0);

  const grandTotal = (year: number): number =>
    orderedDeps.reduce((s, d) => s + displayValue(d, year), 0);

  // Saisie : decimales autorisees (max 2), separateur , ou . borne a 100.
  const sanitizePct = (value: string): string => {
    let v = value.replace(',', '.').replace(/[^0-9.]/g, '');
    const dot = v.indexOf('.');
    if (dot !== -1) {
      const intPart = v.slice(0, dot);
      const dec = v.slice(dot + 1).replace(/\./g, '').slice(0, 2);
      v = `${intPart}.${dec}`;
    }
    const n = parseFloat(v || '0');
    if (Number.isFinite(n) && n > 100) v = '100';
    return v;
  };

  const flushSaves = () => {
    const annees = Array.from(dirtyYears.current);
    if (!annees.length) { setSaveStatus('idle'); return; }

    const valides: { year: number; repartition: { id_departement: number; pourcentage: number }[] }[] = [];
    let hasOver = false;
    annees.forEach((year) => {
      // Le tampon garantit 100 % sauf si l'acheteur a sur-alloue les autres lignes (> 100).
      if (grandTotal(year) > 100.05) { hasOver = true; return; }
      const repartition = editableDeps
        .map((d) => ({ id_departement: d, pourcentage: round2(displayValue(d, year)) }))
        .filter((r) => r.pourcentage > 0);
      valides.push({ year, repartition });
    });

    if (!valides.length) { setSaveStatus(hasOver ? 'pending' : 'idle'); return; }

    setSaveStatus('saving');
    Promise.all(
      valides.map(({ year, repartition }) =>
        apiService.saveLocalisationAnnee(year, repartition).then((d: { derniere_maj?: DerniereMaj | null } | null) => {
          dirtyYears.current.delete(year);
          if (d && 'derniere_maj' in d) onMajChange?.(d.derniere_maj ?? null);
          return { year, repartition };
        })),
    )
      .then((saved) => {
        // Refleter en local ce qui est persiste : on remplace chaque annee sauvee dans data.previsions
        // et on vide les saisies correspondantes (le serveur a supprime les cellules a 0 / hors payload).
        setData((prev) => {
          if (!prev) return prev;
          const previsions: Record<string, Record<string, number>> = {};
          Object.entries(prev.previsions || {}).forEach(([dep, byYear]) => { previsions[dep] = { ...byYear }; });
          saved.forEach(({ year, repartition }) => {
            Object.keys(previsions).forEach((dep) => { delete previsions[dep][String(year)]; });
            repartition.forEach((r) => {
              previsions[String(r.id_departement)] = { ...(previsions[String(r.id_departement)] || {}), [String(year)]: r.pourcentage };
            });
          });
          return { ...prev, previsions };
        });
        setEdits((prev) => {
          const next: Record<string, Record<number, string>> = {};
          Object.entries(prev).forEach(([dep, byYear]) => {
            const copy = { ...byYear };
            saved.forEach(({ year }) => { delete copy[year]; });
            next[dep] = copy;
          });
          return next;
        });

        if (hasOver) {
          setSaveStatus('pending');
        } else {
          setSaveStatus('saved');
          window.setTimeout(() => setSaveStatus((s) => (s === 'saved' ? 'idle' : s)), 2500);
        }
      })
      .catch(() => setSaveStatus('idle'));
  };

  const handleChange = (depId: number, year: number, value: string) => {
    if (isBalancer(depId) || !isEditableDep(depId)) return; // tampon ou departement hors perimetre : non editable
    const v = sanitizePct(value);
    // Une valeur saisie sur une annee se propage aux annees SUIVANTES (jamais aux precedentes) :
    // l'acheteur remplit une annee, les suivantes suivent, puis peut en re-editer une plus tard.
    const cibles = years.filter((y) => isEditable(y) && y >= year);
    setEdits((prev) => {
      const depEdits = { ...(prev[String(depId)] || {}) };
      cibles.forEach((y) => { depEdits[y] = v; });
      return { ...prev, [String(depId)]: depEdits };
    });
    cibles.forEach((y) => dirtyYears.current.add(y));
    setSaveStatus('pending');
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => flushRef.current(), 1500);
  };

  const flushRef = useRef(flushSaves);
  flushRef.current = flushSaves;
  useEffect(() => () => {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    flushRef.current();
  }, []);

  // Annees editables dont le total depasse 100 % (seul etat invalide possible avec le tampon).
  const overYears = years.filter((y) => isEditable(y) && grandTotal(y) > 100.05);

  const showSkeleton = !ready;
  const skelDeps = zones.reduce((n, z) => n + z.departements.length, 0);
  const skeletonRowCount = skelDeps > 0 ? skelDeps + zones.length : 8;

  return (
    <>
      <SectionPliable
        className="estimation-besoins-block"
        title={(
          <>
            Localisation en % du volume de commande
            <span className="estimation-besoins-asterisk">*</span>
          </>
        )}
        headerRight={(saveStatus === 'idle' || overYears.length > 0) ? undefined : (
          <span className={`localisation-savestatus localisation-savestatus--${saveStatus}`}>
            <span className="localisation-savestatus-dot" aria-hidden="true" />
            {saveStatus === 'pending' && 'Modifications en attente'}
            {saveStatus === 'saving' && 'Enregistrement…'}
            {saveStatus === 'saved' && 'Enregistré'}
          </span>
        )}
      >
        <Banner
          variant="info"
          className="estimation-besoins-notice"
          message="Répartissez votre volume de commande par zone géographique (décimales autorisées). La dernière ligne s'ajuste automatiquement pour que chaque année totalise 100 %. Les années passées sont calculées à partir de vos commandes. Vous pouvez corriger le prévisionnel des années futures."
        />
        {!showSkeleton && overYears.length > 0 && (
          <Banner
            variant="danger"
            className="estimation-besoins-notice"
            message={overYears.length === 1
              ? `Le total de ${overYears[0]} dépasse 100 %. Réduisez une zone pour pouvoir enregistrer.`
              : `Le total dépasse 100 % pour ${overYears.join(', ')}. Réduisez une zone pour pouvoir enregistrer.`}
          />
        )}
        <div className="estimation-besoins-table-zone">
        {(canScrollLeft || canScrollRight) && (
        <div
          className="estimation-besoins-carousel-bar"
          style={{ left: arrowBox.left, right: arrowBox.right, height: arrowBox.height || undefined, paddingLeft: arrowBox.pad, paddingRight: arrowBox.pad }}
        >
          <button type="button" className="estimation-besoins-arrow" onClick={() => scrollYears(-1)} disabled={!canScrollLeft} aria-label="Années précédentes">‹</button>
          <button type="button" className="estimation-besoins-arrow" onClick={() => scrollYears(1)} disabled={!canScrollRight} aria-label="Années suivantes">›</button>
        </div>
        )}
        <div className="estimation-besoins-table-wrap" ref={setWrap}>
          <table className="estimation-besoins-table" ref={tableRef}>
            <colgroup>
              <col className="cg-lot" />
              <col className="cg-libelle" />
              {years.map((y) => <col key={y} className="cg-annee" />)}
            </colgroup>
            <thead>
              <tr>
                <th className="col-lot" ref={lotThRef}>N°</th>
                <th className="col-libelle" ref={libelleThRef}>Lot géographique</th>
                {years.map((y, idx) => (
                  <th
                    key={y}
                    ref={(el) => { if (idx === 0) yearColRef.current = el; }}
                    className={isEditable(y) ? 'col-annee' : 'col-annee is-past'}
                  >
                    {y}
                    {isEditable(y) && <span className="estimation-besoins-previsionnel">(prévisionnel)</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {showSkeleton
                ? Array.from({ length: skeletonRowCount }).map((_, i) => (
                    <tr key={`sk${i}`} className="estim-skel-row">
                      <td className="col-lot"><span className="estim-skel-bar estim-skel-bar--code" /></td>
                      <td className="col-libelle"><span className="estim-skel-bar estim-skel-bar--lot" /></td>
                      {years.map((y) => <td key={y} className="col-annee"><span className="estim-skel-bar" /></td>)}
                    </tr>
                  ))
                : zones.map((zone) => (
                  <React.Fragment key={zone.id_region}>
                    {/* En-tete de region : agregat (lecture seule) de ses departements. */}
                    <tr className="localisation-region-row">
                      <td className="col-lot">{zone.id_region}</td>
                      <td className="col-libelle">
                        <span className="col-libelle-text">{zone.region_nom}</span>
                      </td>
                      {years.map((y) => (
                        <td key={y} className={isEditable(y) ? 'col-annee' : 'col-annee is-past'}>{fmtPct(regionTotal(zone, y))}</td>
                      ))}
                    </tr>
                    {zone.departements.map((dep) => (
                      <tr key={dep.id}>
                        <td className="col-lot">{dep.id}</td>
                        <td
                          className="col-libelle"
                          onMouseEnter={(e) => {
                            if (!dep.nom) return;
                            const r = e.currentTarget.getBoundingClientRect();
                            setTip({ x: r.left, y: r.bottom + 4, text: dep.nom });
                          }}
                          onMouseLeave={() => setTip(null)}
                        >
                          <span className="col-libelle-text localisation-dep-libelle">{dep.nom}</span>
                          {!isEditableDep(dep.id) ? (
                            <span className="localisation-hist-badge" title="Département retiré du périmètre : historique conservé, non modifiable">hors périmètre</span>
                          ) : isBalancer(dep.id) ? (
                            <span className="localisation-auto-label" title="Cette ligne s'ajuste automatiquement pour atteindre 100 %">Automatique</span>
                          ) : null}
                        </td>
                        {years.map((y) => {
                          const futureEditable = isEditable(y) && isEditableDep(dep.id);
                          const balancer = futureEditable && isBalancer(dep.id);
                          const showInput = futureEditable && !balancer;
                          const horsPerimetreFutur = isEditable(y) && !isEditableDep(dep.id);
                          const focused = focusedKey === keyOf(dep.id, y);
                          const tdClass = showInput ? 'col-annee is-editable' : (balancer ? 'col-annee is-balancer' : 'col-annee is-past');
                          const val = displayValue(dep.id, y);
                          return (
                            <td key={y} className={tdClass}>
                              {horsPerimetreFutur ? (
                                <span className="estimation-besoins-readonly">—</span>
                              ) : (
                                <span className="localisation-cell">
                                  <span className="localisation-gauge" aria-hidden="true">
                                    <span className="localisation-gauge-fill" style={{ width: `${Math.max(0, Math.min(100, val))}%` }} />
                                  </span>
                                  {showInput ? (
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      className="estimation-besoins-input localisation-input"
                                      value={focused ? rawValue(dep.id, y) : String(round2(editableNumber(dep.id, y)))}
                                      onChange={(e) => handleChange(dep.id, y, e.target.value)}
                                      onFocus={(e) => { setFocusedKey(keyOf(dep.id, y)); const el = e.currentTarget; requestAnimationFrame(() => { if (document.activeElement === el) el.select(); }); }}
                                      onBlur={() => { setFocusedKey(null); if (saveTimer.current) window.clearTimeout(saveTimer.current); flushRef.current(); }}
                                      placeholder="0"
                                      aria-label={`${dep.nom} ${y}`}
                                    />
                                  ) : (
                                    <span
                                      className={`localisation-readonly-val${balancer ? ' localisation-balancer-value' : ''}`}
                                      title={balancer ? 'Ajusté automatiquement pour atteindre 100 %' : undefined}
                                    >
                                      {round2(val)}
                                    </span>
                                  )}
                                  <span className="localisation-cell-pct">%</span>
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              {/* Ajout d'un departement (au-dela du perimetre). */}
              {!showSkeleton && (
                <tr className="estimation-besoins-nouveau-row">
                  <td className="col-lot" />
                  <td className="col-libelle">
                    <Button variant="secondary" className="estimation-besoins-suggest-btn" icon="+" value="Modifier mon périmètre" onClick={() => setPerimetreModalOpen(true)} />
                  </td>
                  {years.map((y) => <td key={y} className="col-annee" />)}
                </tr>
              )}
            </tbody>
            <tfoot>
              {showSkeleton ? (
                <tr className="estim-skel-row">
                  <td className="foot-label" colSpan={2}><span className="estim-skel-bar estim-skel-bar--lot" /></td>
                  {years.map((y) => <td key={y}><span className="estim-skel-bar" /></td>)}
                </tr>
              ) : (
                <tr className="estimation-besoins-total">
                  <td className="foot-label" colSpan={2}>Total</td>
                  {years.map((y) => {
                    const total = grandTotal(y);
                    const off = Math.abs(total - 100) > (isEditable(y) ? 0.05 : 0.5);
                    return (
                      <td key={y} className={`${isEditable(y) ? '' : 'is-past'}${off ? ' localisation-total-off' : ''}`}>
                        {fmtPct(total)}
                      </td>
                    );
                  })}
                </tr>
              )}
            </tfoot>
          </table>
        </div>
        </div>
      </SectionPliable>
      {tip && (
        <div className="estimation-besoins-tip" style={{ left: tip.x, top: tip.y }}>{tip.text}</div>
      )}
      <PerimetreCarteModal
        isOpen={perimetreModalOpen}
        onClose={() => setPerimetreModalOpen(false)}
        initialValue={data?.perimetre_detail ?? { type: 'zones', regions: [], departements: [] }}
        saving={perimetreSaving}
        onValidate={handleSavePerimetre}
      />
    </>
  );
};

export default LocalisationBesoins;
